/**
 * Animal Avatar Service (Hybrid approach)
 * Phase 1: Reads from JSON config
 * Phase 2: Will read from Supabase with fallback to config
 */

import {
  ANIMALS,
  AVATAR_ITEMS,
  STARTER_ANIMAL_IDS,
  getUnlockedAnimals,
  getUnlockedItems,
  getAnimalById,
  getAvatarItemById,
  isAnimalUnlocked,
  isItemUnlocked,
} from '@/config/avatars.config'
import type {
  AnimalAvatar,
  AvatarItem,
  ChildAvatarConfig,
  AvatarItemsByType,
  UnlockedItems,
} from '@/models/Avatar'
import { supabase } from '@/lib/supabase'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isValidUuid(id: string): boolean {
  return UUID_REGEX.test(id)
}

/**
 * Get all available animals from config
 */
export function getAllAnimals(): AnimalAvatar[] {
  return ANIMALS
}

/**
 * Get all available avatar items from config
 */
export function getAllAvatarItems(): AvatarItem[] {
  return AVATAR_ITEMS
}

/**
 * Get animals unlocked for a specific child level
 */
export function getAnimalsForLevel(level: number): AnimalAvatar[] {
  return getUnlockedAnimals(level)
}

/**
 * Get avatar items unlocked for a specific child level
 * Optionally filter by animal ID and/or item type
 */
export function getItemsForLevel(
  level: number,
  animalId?: string,
  itemType?: string
): AvatarItem[] {
  return getUnlockedItems(level, animalId, itemType)
}

/**
 * Get all unlocked content for a child based on their level
 */
export function getUnlockedContent(level: number, animalId?: string): UnlockedItems {
  return {
    animals: getUnlockedAnimals(level),
    items: getUnlockedItems(level, animalId),
  }
}

/**
 * Group avatar items by type (poses, accessories, backgrounds)
 */
export function groupItemsByType(items: AvatarItem[]): AvatarItemsByType {
  return {
    poses: items.filter((item) => item.type === 'pose'),
    accessories: items.filter((item) => item.type === 'accessory'),
    backgrounds: items.filter((item) => item.type === 'background'),
  }
}

/**
 * Get animal by ID
 */
export function getAnimal(animalId: string): AnimalAvatar | undefined {
  return getAnimalById(animalId)
}

/**
 * Get avatar item by ID
 */
export function getAvatarItem(itemId: string): AvatarItem | undefined {
  return getAvatarItemById(itemId)
}

/**
 * Check if animal is unlocked for level
 */
export function checkAnimalUnlocked(animalId: string, level: number): boolean {
  return isAnimalUnlocked(animalId, level)
}

/**
 * Check if item is unlocked for level
 */
export function checkItemUnlocked(itemId: string, level: number): boolean {
  return isItemUnlocked(itemId, level)
}

// ===== DATABASE OPERATIONS (Child Avatar Config) =====

/**
 * Get child's avatar configuration from database
 */
export async function getChildAvatarConfig(
  childId: string
): Promise<ChildAvatarConfig | null> {
  if (!isValidUuid(childId)) return null
  try {
    const { data, error } = await supabase
      .from('child_avatars')
      .select('*')
      .eq('child_id', childId)
      .single()

    if (error) {
      // No config found is not an error, just return null
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching child avatar config:', error)
      return null
    }

    if (!data) {
      return null
    }

    // Convert database format to our model
    return {
      childId: data.child_id,
      animalId: data.animal_id || 'dog', // Default to dog (first animal at level 0)
      equippedPose: data.equipped_pose,
      equippedAccessories: data.equipped_accessories || [],
      equippedBackground: data.equipped_background,
    }
  } catch (err) {
    console.error('Error in getChildAvatarConfig:', err)
    return null
  }
}

/**
 * Batch variant of getChildAvatarConfig: fetch every child's avatar config in a
 * single query instead of one request per child. The Rewards/Tasks tab Swipers
 * keep one slide mounted per kid, so the per-child fetch showed up in Sentry as
 * an N+1 (child_avatars?child_id=*, HARMONIA-7). Returns a map keyed by childId;
 * children without a row are simply absent.
 */
export async function getChildAvatarConfigs(
  childIds: string[]
): Promise<Map<string, ChildAvatarConfig>> {
  const result = new Map<string, ChildAvatarConfig>()
  const validIds = childIds.filter(isValidUuid)
  if (validIds.length === 0) return result

  try {
    const { data, error } = await supabase
      .from('child_avatars')
      .select('*')
      .in('child_id', validIds)

    if (error) {
      console.error('Error fetching child avatar configs:', error)
      return result
    }

    for (const row of (data ?? []) as any[]) {
      result.set(row.child_id, {
        childId: row.child_id,
        animalId: row.animal_id || 'dog', // Default to dog (first animal at level 0)
        equippedPose: row.equipped_pose,
        equippedAccessories: row.equipped_accessories || [],
        equippedBackground: row.equipped_background,
      })
    }
  } catch (err) {
    console.error('Error in getChildAvatarConfigs:', err)
  }

  return result
}

/**
 * Save or update child's avatar configuration
 */
export async function saveChildAvatarConfig(
  config: ChildAvatarConfig
): Promise<{ success: boolean; error?: string }> {
  if (!isValidUuid(config.childId)) {
    return { success: false, error: 'Temporary child ID — skipping DB write' }
  }
  try {
    const { error } = await supabase.from('child_avatars').upsert(
      {
        child_id: config.childId,
        animal_id: config.animalId,
        equipped_pose: config.equippedPose,
        equipped_accessories: config.equippedAccessories,
        equipped_background: config.equippedBackground,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'child_id',
      }
    )

    if (error) {
      console.error('Error saving child avatar config:', error)
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err: any) {
    console.error('Error in saveChildAvatarConfig:', err)
    return { success: false, error: err.message || 'Unknown error' }
  }
}

/**
 * Helper function to create a gift purchase record for a default avatar
 * This ensures that default avatars assigned during onboarding remain available
 */
async function createGiftPurchaseRecord(
  childId: string,
  animalId: string,
  poseId?: string,
  backgroundId?: string
): Promise<void> {
  try {
    // Get all shop items of the relevant types and filter in JavaScript
    // This is more reliable than trying to query JSON metadata directly
    const { data: allShopItems, error: shopError } = await supabase
      .from('shop_items' as any)
      .select('id, type, metadata')
      .eq('is_active', true)
      .in('type', ['animal', 'pose', 'background'])

    if (shopError || !allShopItems || !Array.isArray(allShopItems)) {
      console.warn('Error fetching shop items for gift purchase record:', shopError)
      return
    }

    const shopItemIds: string[] = []
    const shopItemsArray = allShopItems as any[]

    // Find avatar shop_item
    const avatarItem = shopItemsArray.find((item: any) => {
      return item?.type === 'animal' && item?.metadata?.animal_id === animalId
    })
    if (avatarItem && typeof avatarItem.id === 'string') {
      shopItemIds.push(avatarItem.id)
    }

    // Find pose shop_item if provided
    if (poseId) {
      const poseItem = shopItemsArray.find((item: any) => {
        return item?.type === 'pose' &&
          item?.metadata?.pose_id === poseId &&
          item?.metadata?.animal_id === animalId
      })
      if (poseItem && typeof poseItem.id === 'string') {
        shopItemIds.push(poseItem.id)
      }
    }

    // Find background shop_item if provided
    if (backgroundId) {
      const backgroundItem = shopItemsArray.find((item: any) => {
        return item?.type === 'background' && item?.metadata?.background_id === backgroundId
      })
      if (backgroundItem && typeof backgroundItem.id === 'string') {
        shopItemIds.push(backgroundItem.id)
      }
    }

    // Create purchase records for found shop items
    if (shopItemIds.length > 0) {
      // Check if purchase records already exist to avoid duplicates
      const { data: existingPurchases } = await supabase
        .from('child_shop_purchases' as any)
        .select('shop_item_id')
        .eq('child_id', childId)
        .in('shop_item_id', shopItemIds)

      const existingShopItemIds = new Set(
        (existingPurchases || []).map((p: any) => p.shop_item_id)
      )

      // Only create records for items that don't already exist
      const newShopItemIds = shopItemIds.filter(id => !existingShopItemIds.has(id))

      if (newShopItemIds.length > 0) {
        const purchaseRecords = newShopItemIds.map(shopItemId => ({
          child_id: childId,
          shop_item_id: shopItemId,
          coins_spent: 0,
          is_gifted: true,
          is_equipped: false, // Will be set to true when actually equipped
        }))

        const { error: purchaseError } = await supabase
          .from('child_shop_purchases' as any)
          .insert(purchaseRecords)

        if (purchaseError) {
          console.warn('Error creating gift purchase records for default avatar:', purchaseError)
          // Don't throw - this is non-critical, avatar config is still saved
        } else {
          console.log(`Created ${purchaseRecords.length} gift purchase records for default avatar items`)
        }
      }
    }
  } catch (error) {
    console.warn('Error in createGiftPurchaseRecord:', error)
    // Don't throw - this is non-critical, avatar config is still saved
  }
}

/**
 * Initialize default avatar config for a new child
 * Assigns a random background from level 0 available backgrounds
 * Also creates gift purchase records to ensure the avatar remains available
 */
export async function initializeChildAvatar(
  childId: string,
  animalId: string = 'dog' // Default to dog (first animal at level 0)
): Promise<{ success: boolean; error?: string }> {
  // Only allow starter animals (dog/fox) during initialization
  if (!(STARTER_ANIMAL_IDS as readonly string[]).includes(animalId)) {
    animalId = 'dog'
  }

  const animal = getAnimal(animalId)
  if (!animal) {
    return { success: false, error: 'Invalid animal ID' }
  }

  // Find default pose for this animal (the one with the lowest unlock level)
  const animalPoses = AVATAR_ITEMS
    .filter((item) => item.type === 'pose' && item.animalId === animalId)
    .sort((a, b) => a.unlockLevel - b.unlockLevel);
  const defaultPose = animalPoses[0]

  // Get starting backgrounds (level 0) and select a random one
  const startingBackgrounds = AVATAR_ITEMS.filter(
    (item) => item.type === 'background' && item.unlockLevel === 0
  )
  const randomBackground = startingBackgrounds.length > 0
    ? startingBackgrounds[Math.floor(Math.random() * startingBackgrounds.length)]
    : undefined

  const config: ChildAvatarConfig = {
    childId,
    animalId,
    equippedPose: defaultPose?.id,
    equippedAccessories: [],
    equippedBackground: randomBackground?.id,
  }

  // Save avatar config
  const result = await saveChildAvatarConfig(config)

  if (!result.success) {
    return result
  }

  // Create gift purchase records to ensure the avatar remains available
  // This prevents the avatar from becoming locked when the child changes to another avatar
  await createGiftPurchaseRecord(
    childId,
    animalId,
    defaultPose?.id,
    randomBackground?.id
  )

  return { success: true }
}

/**
 * Update child's selected animal.
 * Supports both config animals (ANIMALS) and shop-only animals (from shop_items).
 * For shop-only animals, the default pose is fetched from the shop (cheapest pose for that animal).
 */
export async function updateChildAnimal(
  childId: string,
  animalId: string
): Promise<{ success: boolean; error?: string }> {
  const currentConfig = await getChildAvatarConfig(childId)

  let defaultPoseId: string | undefined

  const configAnimal = getAnimal(animalId)
  if (configAnimal) {
    // Animal from static config: use AVATAR_ITEMS for default pose
    const animalPoses = AVATAR_ITEMS
      .filter((item) => item.type === 'pose' && item.animalId === animalId)
      .sort((a, b) => a.unlockLevel - b.unlockLevel)
    defaultPoseId = animalPoses[0]?.id
  } else {
    // Shop-only animal: fetch default pose from shop_items (cheapest pose for this animal)
    const { data: poses } = await supabase
      .from('shop_items' as any)
      .select('id, metadata')
      .eq('type', 'pose')
      .eq('is_active', true)
      .order('price', { ascending: true })

    const animalPoses = ((poses as any[]) || []).filter(
      (p: any) => p.metadata?.animal_id === animalId
    )
    defaultPoseId = animalPoses[0]?.metadata?.pose_id
  }

  const config: ChildAvatarConfig = {
    childId,
    animalId,
    equippedPose: defaultPoseId,
    equippedAccessories: [], // Reset accessories when changing animal
    equippedBackground: currentConfig?.equippedBackground, // Keep background
  }

  return saveChildAvatarConfig(config)
}

/**
 * Check if the child has access to an animal (unlocked by level or purchased in shop)
 */
export async function childHasAnimal(
  childId: string,
  animalId: string,
  childLevel?: number
): Promise<boolean> {
  if (childLevel !== undefined && childLevel !== null && isAnimalUnlocked(animalId, childLevel)) {
    return true
  }
  const { data: purchases } = await supabase
    .from('child_shop_purchases' as any)
    .select('shop_item:shop_items(type, metadata)')
    .eq('child_id', childId)
  const rows = (purchases || []) as any[]
  // L'animal lui-même, ou n'importe laquelle de ses poses : un animal est une
  // catégorie, il se débloque par ses poses (2026-09-17).
  return rows.some(
    (p: any) =>
      (p.shop_item?.type === 'animal' || p.shop_item?.type === 'pose')
      && p.shop_item?.metadata?.animal_id === animalId
  )
}

/**
 * Equip an avatar item (pose, accessory, or background)
 * If the item is for a different animal than current, switches to that animal when the child has it (level or purchase).
 */
export async function equipAvatarItem(
  childId: string,
  itemId: string,
  options?: { childLevel?: number; itemOverride?: AvatarItem }
): Promise<{ success: boolean; error?: string }> {
  // Use provided item data (shop-only items) or fall back to static config
  const item = options?.itemOverride || getAvatarItem(itemId)
  if (!item) {
    return { success: false, error: 'Invalid item ID' }
  }

  // Get current config
  const config = await getChildAvatarConfig(childId)
  if (!config) {
    return { success: false, error: 'Child avatar not initialized' }
  }

  // Universal items are always compatible
  if (item.animalId === 'all') {
    // continue to apply
  } else if (item.animalId === config.animalId) {
    // Same animal, apply
  } else {
    // Item is for another animal: allow if child has that animal, and switch to it
    const hasAnimal = await childHasAnimal(childId, item.animalId, options?.childLevel)
    if (!hasAnimal) {
      return { success: false, error: 'Item not compatible with selected animal' }
    }
    config.animalId = item.animalId
  }

  // Update appropriate field based on item type
  switch (item.type) {
    case 'pose':
      config.equippedPose = itemId
      break
    case 'accessory':
      // Add accessory if not already equipped
      if (!config.equippedAccessories.includes(itemId)) {
        config.equippedAccessories.push(itemId)
      }
      break
    case 'background':
      config.equippedBackground = itemId
      break
  }

  return saveChildAvatarConfig(config)
}

/**
 * Unequip an accessory
 */
export async function unequipAccessory(
  childId: string,
  itemId: string
): Promise<{ success: boolean; error?: string }> {
  const config = await getChildAvatarConfig(childId)
  if (!config) {
    return { success: false, error: 'Child avatar not initialized' }
  }

  config.equippedAccessories = config.equippedAccessories.filter((id) => id !== itemId)

  return saveChildAvatarConfig(config)
}

/**
 * Get full avatar data for display (config + actual item objects)
 */
export async function getChildAvatarDisplay(childId: string) {
  const config = await getChildAvatarConfig(childId)
  if (!config) {
    return null
  }

  const animal = getAnimal(config.animalId)
  const pose = config.equippedPose ? getAvatarItem(config.equippedPose) : undefined
  const accessories = config.equippedAccessories
    .map((id) => getAvatarItem(id))
    .filter((item): item is AvatarItem => item !== undefined)
  const background = config.equippedBackground
    ? getAvatarItem(config.equippedBackground)
    : undefined

  return {
    config,
    animal,
    pose,
    accessories,
    background,
  }
}
